// Bridges the bot's SSE /events stream to React Query's cache.
// Mount once near the root (inside QueryProvider). Each topic the bot emits
// triggers targeted invalidation of the queries that depend on it, which
// replaces the 5s polling with push delivery (~sub-second latency).
//
// Polling is NOT fully removed — each hook keeps a longer interval (30s) as a
// safety net in case the stream misses an event (reconnect races, bot restart
// while UI is backgrounded, etc).

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  subscribeEvents,
  eventStreamStatus,
  onStatusChange,
  type StreamMessage,
  type StreamStatus,
} from "@/lib/event-stream";

export type { StreamStatus };

/** Mount-once hook: opens the SSE connection and wires topic → invalidate. */
export function useEventStream(): StreamStatus {
  const qc = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>(eventStreamStatus());

  useEffect(() => {
    const unsubscribe = subscribeEvents((msg: StreamMessage) => {
      // Each topic maps to the minimum set of queries affected. Prefer
      // per-entity keys when scope is known, fall back to predicate-based
      // invalidation for broad topics.
      switch (msg.topic) {
        case "grids":
          qc.invalidateQueries({ queryKey: ["grids"] });
          qc.invalidateQueries({ queryKey: ["overview"] });
          if (msg.grid_id) {
            qc.invalidateQueries({ queryKey: ["grid-detail", msg.grid_id] });
            qc.invalidateQueries({ queryKey: ["grid-pnl", msg.grid_id] });
          }
          break;
        case "orders":
          qc.invalidateQueries({ queryKey: ["overview"] });
          if (msg.grid_id) {
            qc.invalidateQueries({ queryKey: ["grid-detail", msg.grid_id] });
          } else {
            // Unscoped order event — conservative broad invalidation.
            qc.invalidateQueries({
              predicate: q => q.queryKey[0] === "grid-detail",
            });
          }
          break;
        case "trades":
          qc.invalidateQueries({ queryKey: ["overview"] });
          if (msg.grid_id) {
            qc.invalidateQueries({ queryKey: ["grid-detail", msg.grid_id] });
            qc.invalidateQueries({ queryKey: ["grid-pnl", msg.grid_id] });
          } else {
            qc.invalidateQueries({
              predicate: q =>
                q.queryKey[0] === "grid-detail" || q.queryKey[0] === "grid-pnl",
            });
          }
          break;
        case "activity":
          qc.invalidateQueries({
            predicate: q => q.queryKey[0] === "activity",
          });
          // Grid detail card surfaces latest AI advice from activity log.
          if (msg.grid_id) {
            qc.invalidateQueries({ queryKey: ["grid-detail", msg.grid_id] });
          }
          if (msg.session_id) {
            qc.invalidateQueries({ queryKey: ["wind-detail", msg.session_id] });
          }
          break;
        case "wind":
          qc.invalidateQueries({ queryKey: ["wind"] });
          qc.invalidateQueries({ queryKey: ["overview"] });
          if (msg.session_id) {
            qc.invalidateQueries({ queryKey: ["wind-detail", msg.session_id] });
          }
          break;
        case "pnl-snapshot":
          // Per-grid pnl sparkline / APR metric pulls from snapshots.
          if (msg.grid_id) {
            qc.invalidateQueries({ queryKey: ["grid-pnl", msg.grid_id] });
          }
          break;
        case "overview-snapshot":
          // useOverviewHistory uses ["history", range] — invalidate all ranges.
          qc.invalidateQueries({
            predicate: q => q.queryKey[0] === "history",
          });
          break;
      }
    });

    // Transport-level status pushed via pub/sub — no polling needed.
    const unsubscribeStatus = onStatusChange(setStatus);

    return () => {
      unsubscribe();
      unsubscribeStatus();
    };
  }, [qc]);

  return status;
}

/** Read-only hook for components that need status but don't drive the
 *  connection (the connection is owned by <RealtimeBridge />). Avoids
 *  accidentally opening/closing the shared EventSource from a leaf node. */
export function useEventStreamStatus(): StreamStatus {
  const [status, setStatus] = useState<StreamStatus>(eventStreamStatus());
  useEffect(() => onStatusChange(setStatus), []);
  return status;
}
